import { LitElement, html, css } from 'lit';

export class UISignature extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    value: { type: String, reflect: true }, // Base64 dataURL
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    color: { type: String }, // Drawing ink color
    lineWidth: { type: Number },
    label: { type: String, reflect: true },
    
    // Internal States
    isEmpty: { type: Boolean, state: true }
  };

  static styles = css`
    :host {
      display: inline-block;
      width: 100%;
      font-family: var(--ui-font-family);
      font-size: var(--ui-font-size-base);
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .signature-container {
      position: relative;
      width: 100%;
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-lg);
      background-color: #ffffff; /* Signatures are drawn on white background */
      box-shadow: var(--ui-shadow-sm);
      overflow: hidden;
      box-sizing: border-box;
    }

    .signature-container:hover:not(.disabled) {
      border-color: var(--ui-border-hover);
    }

    .signature-container.focused:not(.disabled) {
      border-color: var(--ui-border-focus);
      box-shadow: 0 0 0 4px var(--ui-focus-ring);
    }

    .signature-container.disabled {
      opacity: 0.6;
      background-color: var(--ui-surface-active);
    }

    canvas {
      display: block;
      width: 100%;
      height: 180px;
      cursor: crosshair;
      touch-action: none; /* Disable scrolling/gestures while drawing */
    }

    .signature-header {
      position: absolute;
      top: 10px;
      right: 10px;
      display: flex;
      gap: 8px;
      z-index: 2;
    }

    .clear-btn {
      border: 1px solid var(--ui-border);
      background-color: rgba(255, 255, 255, 0.9);
      backdrop-filter: blur(4px);
      padding: 6px 10px;
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      color: var(--ui-text-muted);
      border-radius: var(--ui-radius-sm);
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 4px;
      transition: var(--ui-transition-fast);
      box-shadow: var(--ui-shadow-sm);
    }

    .clear-btn:hover:not(:disabled) {
      background-color: #ffffff;
      color: var(--ui-danger);
      border-color: var(--ui-danger);
    }

    .clear-btn:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    .signature-baseline-label {
      position: absolute;
      bottom: 12px;
      left: 50%;
      transform: translateX(-50%);
      font-size: var(--ui-font-size-sm);
      color: var(--ui-text-light);
      pointer-events: none;
      user-select: none;
      transition: opacity var(--ui-transition-fast);
    }

    .signature-baseline-label.fade {
      opacity: 0;
    }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.value = '';
    this.disabled = false;
    this.required = false;
    this.color = '#1f2937';
    this.lineWidth = 2.5;
    this.label = '';

    this.isEmpty = true;
    this._isDrawing = false;
    this._prevX = 0;
    this._prevY = 0;

    this._resizeCanvas = this._resizeCanvas.bind(this);
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  updated(changedProperties) {
    if (changedProperties.has('value') || changedProperties.has('required')) {
      this.internals.setFormValue(this.value);
      this._updateValidity();
    }
  }

  _updateValidity() {
    if (this.required && this.isEmpty) {
      this.internals.setValidity(
        { valueMissing: true },
        'Veuillez signer dans le cadre.',
        this.shadowRoot.querySelector('canvas')
      );
    } else {
      this.internals.setValidity({});
    }
  }

  firstUpdated() {
    this._canvas = this.shadowRoot.querySelector('canvas');
    this._ctx = this._canvas.getContext('2d');
    
    this._resizeCanvas();
    window.addEventListener('resize', this._resizeCanvas);
    
    // Set drawing styles
    this._ctx.lineCap = 'round';
    this._ctx.lineJoin = 'round';
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this._resizeCanvas);
  }

  _resizeCanvas() {
    if (!this._canvas) return;

    const rect = this._canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    
    // Store current drawings as image if not empty
    const currentSignature = this.value;

    this._canvas.width = rect.width * dpr;
    this._canvas.height = rect.height * dpr;
    this._ctx.scale(dpr, dpr);
    
    // Reset styles after resizing
    this._ctx.lineCap = 'round';
    this._ctx.lineJoin = 'round';
    this._ctx.strokeStyle = this.color;
    this._ctx.lineWidth = this.lineWidth;

    this._drawBaseline();

    // Redraw signature if it exists
    if (currentSignature) {
      const img = new Image();
      img.onload = () => {
        this._ctx.drawImage(img, 0, 0, rect.width, rect.height);
      };
      img.src = currentSignature;
    }
  }

  _drawBaseline() {
    const width = this._canvas.width / (window.devicePixelRatio || 1);
    const height = this._canvas.height / (window.devicePixelRatio || 1);
    
    // Dotted line at 75% height
    this._ctx.save();
    this._ctx.beginPath();
    this._ctx.setLineDash([4, 4]);
    this._ctx.strokeStyle = '#d1d5db';
    this._ctx.lineWidth = 1;
    this._ctx.moveTo(30, height * 0.75);
    this._ctx.lineTo(width - 30, height * 0.75);
    this._ctx.stroke();
    this._ctx.restore();
  }

  _getCoordinates(e) {
    const rect = this._canvas.getBoundingClientRect();
    let clientX, clientY;

    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  }

  _startDrawing(e) {
    if (this.disabled) return;
    e.preventDefault();
    this._isDrawing = true;
    
    const coords = this._getCoordinates(e);
    this._prevX = coords.x;
    this._prevY = coords.y;

    // Dispatch event or adjust label fade
    this.isEmpty = false;
  }

  _draw(e) {
    if (!this._isDrawing || this.disabled) return;
    e.preventDefault();

    const coords = this._getCoordinates(e);
    
    this._ctx.beginPath();
    this._ctx.moveTo(this._prevX, this._prevY);
    this._ctx.lineTo(coords.x, coords.y);
    this._ctx.strokeStyle = this.color;
    this._ctx.lineWidth = this.lineWidth;
    this._ctx.stroke();

    this._prevX = coords.x;
    this._prevY = coords.y;
  }

  _stopDrawing() {
    if (!this._isDrawing) return;
    this._isDrawing = false;
    
    this._saveSignature();
  }

  _saveSignature() {
    const dataUrl = this._canvas.toDataURL('image/png');
    this.value = dataUrl;
    
    this.dispatchEvent(new CustomEvent('change', {
      detail: { value: dataUrl },
      bubbles: true,
      composed: true
    }));
  }

  clear() {
    if (this.disabled) return;
    this._ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
    this._drawBaseline();
    
    this.value = '';
    this.isEmpty = true;
    
    this.dispatchEvent(new CustomEvent('change', {
      detail: { value: '' },
      bubbles: true,
      composed: true
    }));
  }

  render() {
    return html`
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div class="signature-container ${this.disabled ? 'disabled' : ''}">
        <div class="signature-header">
          <button 
            type="button" 
            class="clear-btn" 
            ?disabled="${this.disabled || this.isEmpty}"
            @click="${this.clear}"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
            Effacer
          </button>
        </div>

        <canvas part="input"
          @mousedown="${this._startDrawing}"
          @mousemove="${this._draw}"
          @mouseup="${this._stopDrawing}"
          @mouseleave="${this._stopDrawing}"
          
          @touchstart="${this._startDrawing}"
          @touchmove="${this._draw}"
          @touchend="${this._stopDrawing}"
          @touchcancel="${this._stopDrawing}"
        ></canvas>

        <span class="signature-baseline-label ${!this.isEmpty ? 'fade' : ''}">
          Signez ici
        </span>
      </div>
    `;
  }
}

if (!customElements.get('ui-signature')) {
  customElements.define('ui-signature', UISignature);
}
